import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import qs from "query-string";
import { MenuItem, Select, useMediaQuery } from "@mui/material";
import axiosInstant from "../../../../../api";
import { getBrandProjects, getVideoFileAnalytics } from "../../../../../api/endpoints";
import { IVideo } from "../../hooks/useFetchVideos";
import { fancyTimeFormat } from "../../adminVideosV2/helper";
import {
  VideoPanel,
  PanelError,
  PanelSkeleton,
  IconChartGlyph,
  IconThumbsUpGlyph,
  IconEyeGlyph,
  IconClockGlyph,
  IconPlayGlyph,
} from "../panels";
import type { PanelStatus } from "../panels";
import {
  AnalyticsPayload,
  ChartRange,
  computeMetrics,
  dayParam,
  describeRange,
  formatCount,
  formatWatchTime,
  payloadRange,
  viewerTimeZone,
} from "./metrics";
import { SecondsChart } from "./SecondsChart";
import { RangeControl, CONTROL_SX, Labelled } from "./RangeControl";

/**
 * Performance — a page-local rebuild of the Analytics section.
 *
 * PAGE-LOCAL IS THE WHOLE POINT: `pages/admin/videoAnalytics/` is still mounted
 * by the V1 detail page on /videos/ugc, /videos/xgc and /my-videos. Restyling it
 * in place would silently redesign all three. That module is left untouched.
 *
 * Behaviour changes from the old section, all deliberate:
 *   · ONE request, already counted: the API returns totals and a per-day series
 *     for the window (TDC-1676). Nothing is filtered or summed in the browser;
 *     changing the range, source or project asks the server again
 *   · a failure renders an error state instead of being swallowed by an empty
 *     `catch {}` and falling through to "There is no analytic data for this
 *     video" — an outage and an unwatched video no longer look identical
 *   · Plays honours the date range (it read all-time while its three neighbours
 *     were range-filtered)
 *   · units are `1h 45m` / `0:27`, never `1.03 min`
 *   · the source and project pickers have real "All" options and real labels
 *     ("Project", not "Distribution")
 */

interface Props {
  video: IVideo;
  brandId?: number;
}

const StatTile: FC<{
  icon: ReactNode;
  label: string;
  value: string;
  footnote: string;
  dimmed?: boolean;
}> = ({ icon, label, value, footnote, dimmed }) => (
  <div className="border border-border-token rounded-xl" style={{ padding: "14px 16px" }}>
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-accent shrink-0">{icon}</span>
      <span className="truncate text-text-2" style={{ fontSize: 12.5 }}>
        {label}
      </span>
    </div>
    <div
      className={`font-CircularStdBlack tabular-nums ${dimmed ? "text-muted" : "text-text-1"}`}
      style={{ fontSize: 32, lineHeight: 1, marginTop: 10 }}
    >
      {value}
    </div>
    {/* Every tile states its own denominator. That is what fixes the metric
        vocabulary — in words, at the point of reading, rather than in a
        footnote nobody sees. */}
    <div className="text-text-2" style={{ fontSize: 11, marginTop: 6 }}>
      {footnote}
    </div>
  </div>
);

export const PerformancePanel: FC<Props> = ({ video, brandId }) => {
  const [data, setData] = useState<AnalyticsPayload>();
  const [status, setStatus] = useState<PanelStatus>("loading");
  // A re-request while numbers are already on screen (range / filter change)
  // keeps them visible and dims them, instead of flashing the skeleton.
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [source, setSource] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<Array<{ id: number; title: string }>>([]);
  // null = no window asked for: the API answers with the video's whole history
  // (first event → today), which is what the panel has always opened on. The
  // window shown is then the one the payload reports, so the pill never has to
  // guess. A user pick sets it and is sent as from / to.
  const [range, setRange] = useState<ChartRange | null>(null);

  const videoId = video?.id;

  useEffect(() => {
    if (!brandId || !videoId) return undefined;
    let live = true;
    // Skeleton only before the first payload; afterwards dim and refetch.
    setStatus((s) => (s === "ready" ? s : "loading"));
    setRefreshing(true);

    const params = qs.stringify({
      project: projectId || undefined,
      source: source || undefined,
      tz: viewerTimeZone(),
      from: range ? dayParam(range[0]) : undefined,
      to: range ? dayParam(range[1]) : undefined,
    });
    axiosInstant
      .get(getVideoFileAnalytics(brandId, videoId, params))
      .then((res) => {
        if (!live) return;
        setData(res.data);
        setStatus("ready");
        setRefreshing(false);
      })
      .catch(() => {
        if (!live) return;
        setData(undefined);
        setStatus("error");
        setRefreshing(false);
      });
    return () => {
      live = false;
    };
  }, [brandId, videoId, projectId, source, range, attempt]);

  // Same single request the old panel made — every project for the brand.
  useEffect(() => {
    if (!brandId) return undefined;
    let live = true;
    axiosInstant
      .get(getBrandProjects(brandId))
      .then(({ data: res }) => live && setProjects(res?.rows || []))
      .catch(() => live && setProjects([]));
    return () => {
      live = false;
    };
  }, [brandId]);

  // The window on screen: the user's pick, else the one the payload was
  // counted over, else (before the first payload) a placeholder week.
  const shownRange = useMemo<ChartRange>(() => {
    if (range) return range;
    if (data) return payloadRange(data);
    return [dayjs().subtract(6, "day").startOf("day"), dayjs()];
  }, [range, data]);

  const metrics = useMemo(() => computeMetrics(data), [data]);
  const noData = status === "ready" && !metrics.hasAnyData;
  // In error the tiles keep their labels and print "unavailable" rather than a
  // number, so an outage can never be read as a zero (handoff §06).
  const errored = status === "error";
  const statValue = (formatted: string) => (errored ? "—" : formatted);
  const statNote = (note: string) => (errored ? "unavailable" : note);
  const runtime = video?.length ? fancyTimeFormat(video.length) : "";
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Tailwind's `sm` (640px), read in JS because two of the mobile corrections
  // MOVE a node between the panel header and the panel body — DOM positions no
  // media query can bridge. The controls are stateful (an open Select, a range
  // popover mid-pick), so they are rendered ONCE and relocated rather than
  // duplicated behind `hidden` / `sm:block`: two copies would double the tab
  // stops and let a menu open on the one nobody can see. Everything that is
  // purely visual still moves on `sm:` classes; keep this value in step with
  // tailwind.config.js.
  const wide = useMediaQuery("(min-width:640px)", { noSsr: true });

  // The filters stay live in every state. Dimming them was one thing; making them
  // `pointer-events-none` trapped the user: choosing a Source with no traffic emptied
  // the payload, which switched off the very control that had to be changed to get
  // back, and the filters live in component state so a reload was the only way out.
  // The same lock made the error state's "Try again" the only reachable action while
  // it refetched the identical failing filters.
  const control = (node: ReactNode) => node;

  // One node, two homes: the header row from sm up, a block under the numbers
  // below it. The grid classes are what that block is made of — date range full
  // width on its own row, Source and Project 2-up beneath it — and they are all
  // inert once `sm:flex` takes over, so the desktop row is the row it has
  // always been.
  const controls = (
    <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
      {/* Wrapped because the span has to be set from out here: RangeControl
          renders its own `Labelled` and takes no className. */}
      <div className="col-span-2">
        {control(
          <RangeControl range={shownRange} earliest={metrics.earliest} onChange={setRange} />,
        )}
      </div>
      {control(
        <Labelled label="Source">
          <Select
            value={source}
            onChange={(e) => setSource(String(e.target.value))}
            // The desktop minimums are wider than a column of the 2-up grid at
            // 375, where they would overflow it rather than widen it — the
            // track is `minmax(0, 1fr)`. Below sm the column is the width.
            sx={{ ...CONTROL_SX, minWidth: wide ? 132 : 0 }}
          >
            {/* A real "all" option, not a greyed placeholder row. */}
            <MenuItem value="">All sources</MenuItem>
            <MenuItem value="qr">QR</MenuItem>
            <MenuItem value="embed">Embed</MenuItem>
            <MenuItem value="landing">Landing page</MenuItem>
            <MenuItem value="pdp">Product page</MenuItem>
          </Select>
        </Labelled>,
      )}
      {control(
        <Labelled label="Project">
          <Select
            value={projectId}
            onChange={(e) => setProjectId(String(e.target.value))}
            sx={{ ...CONTROL_SX, minWidth: wide ? 148 : 0, maxWidth: 240 }}
          >
            <MenuItem value="">All projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.title}
              </MenuItem>
            ))}
          </Select>
        </Labelled>,
      )}
    </div>
  );

  const header = (
    // VideoPanel puts a `flex-1` spacer between the title and this node, which
    // pins whatever it holds to the far right. That is right for the desktop
    // cluster; below sm the pill is the title's qualifier ("Performance ·
    // all time"), so the action out-grows that spacer and keeps it alongside.
    <div className="flex flex-wrap items-end gap-3 grow-[999] sm:grow-0">
      {/* The range is not known until the payload lands (the API picks the
          window when none was asked for). Hold the pill's space until then. */}
      {status === "loading" ? (
        <span
          className="inline-block h-[22px] w-[72px] rounded-full bg-track animate-pulse motion-reduce:animate-none shrink-0"
          aria-hidden="true"
        />
      ) : (
        <span
          className="inline-flex items-center h-[22px] px-2.5 rounded-full bg-surface-2 text-text-2 shrink-0"
          style={{ fontSize: 11.5 }}
        >
          {describeRange(shownRange, metrics.earliest)}
        </span>
      )}
      <span className="flex-1 min-w-0" />
      {/* Below sm the header is one row of title + pill and nothing else. The
          filter cluster used to ride along here at 375, where it wrapped to
          three lines and left the vertically centred title reading as a label
          for the third control. */}
      {wide && controls}
    </div>
  );

  // The four numbers, declared once: the full tiles and the compressed 2x2
  // below are two renderings of this list, not two lists.
  const stats = [
    {
      icon: <IconThumbsUpGlyph size={15} />,
      label: "Impressions",
      value: statValue(formatCount(metrics.impressions)),
      footnote: statNote("in range"),
    },
    {
      icon: <IconEyeGlyph size={15} />,
      label: "Plays",
      value: statValue(formatCount(metrics.plays)),
      footnote: statNote("in range"),
    },
    {
      icon: <IconClockGlyph size={15} />,
      label: "Watch time",
      value: statValue(formatWatchTime(metrics.watchSeconds)),
      footnote: statNote("in range"),
    },
    {
      icon: <IconPlayGlyph size={15} />,
      label: "Avg play time",
      value: statValue(fancyTimeFormat(Math.round(metrics.avgPlaySeconds))),
      footnote: statNote(runtime ? `of ${runtime} runtime` : "per play"),
    },
  ];

  const tiles = (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
    >
      {stats.map((stat) => (
        // Dimmed in ERROR only. A zero is a true value, and `--muted` on it puts
        // it straight back into the ambiguous absent-or-loading register that
        // printing real numbers instead of em-dashes was meant to escape.
        <StatTile key={stat.label} {...stat} dimmed={errored} />
      ))}
    </div>
  );

  // Below sm, and only while every value in range is 0, the four cards collapse
  // into one hairline-divided 2x2 — about 150px where the cards were 440 — still
  // printing four real numbers. The de-emphasis is carried by the SIZE STEP
  // ALONE, 18px against the tile's 32: the values keep `--text-2` and only the
  // labels drop a notch to `--muted`, so the number still outranks its label
  // without any of them going dim. It reverts to `tiles` the moment one value is
  // non-zero. Icons and the "in range" footnotes go — at this size they are
  // chrome around a zero, and the strip above has already said why it is zero.
  const compressedTiles = (
    <div className="grid grid-cols-2">
      {stats.map(({ label, value }, i) => (
        <div
          key={label}
          // Each cell is padded on its divider side only, so the block sits
          // flush inside the panel's own padding rather than nesting a second
          // inset within it.
          className={`min-w-0 ${i < 2 ? "border-b border-border-token pb-3" : "pt-3"} ${
            i % 2 === 0 ? "border-r border-border-token pr-3.5" : "pl-3.5"
          }`}
        >
          <div className="truncate text-muted" style={{ fontSize: 12.5 }}>
            {label}
          </div>
          <div
            className="font-CircularStdBlack tabular-nums text-text-2"
            style={{ fontSize: 18, lineHeight: 1, marginTop: 6 }}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  );

  const noViews = (
      <div
        className="border border-border-token rounded-xl flex items-center gap-2.5 flex-wrap"
        style={{ padding: "14px 16px" }}
      >
        <span className="text-muted shrink-0">
          <IconChartGlyph size={16} strokeWidth={1.6} />
        </span>
        <span className="font-CircularStdBlack text-text-1" style={{ fontSize: 13 }}>
          No views yet
        </span>
        <span className="text-text-2" style={{ fontSize: 12.5 }}>
          This video hasn&apos;t been embedded or shared, so there&apos;s nothing to measure.
        </span>
      </div>
  );

  const body = () => {
    if (status === "loading") {
      return (
        <div className="flex flex-col gap-4">
          <PanelSkeleton variant="tiles" count={4} />
          <div className="rounded-xl bg-track animate-pulse motion-reduce:animate-none" style={{ height: 240 }} />
        </div>
      );
    }
    // The tiles stay on screen in error — only their values go unavailable.
    if (errored) {
      return (
        <div className="flex flex-col gap-[18px]">
          {tiles}
          <PanelError
            icon={<IconChartGlyph size={24} strokeWidth={1.6} />}
            title="Couldn't load performance data"
            body="Something went wrong on our end. This is not the same as a video with no views."
            onRetry={retry}
            minHeight={200}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-[18px]">
        {noData ? (
          <>
            {/* Correction 2 is MOBILE-ONLY. Trailing four stacked tiles at 375
                made the reader scroll ~440px of zeroes before finding out why
                they were zero; four-across on desktop there is no scroll to
                save, and §02 (the desktop empty state) draws it after the
                tiles. One node, placed on one side or the other — never both.
                Still compact on purpose: the numbers beside it already read
                zero, so the 200px illustrated block this replaced was restating
                that at eight times the size. */}
            {!wide && noViews}
            {wide ? tiles : compressedTiles}
            {wide && noViews}
          </>
        ) : (
          <>
            {tiles}
            <div className="border border-border-token rounded-xl p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span
                  className="font-CircularStdBlack uppercase text-muted"
                  style={{ fontSize: 9.5, letterSpacing: ".12em" }}
                >
                  Seconds watched per day
                </span>
                <span className="text-text-2 tabular-nums" style={{ fontSize: 11.5 }}>
                  {(() => {
                    const peak = metrics.series.reduce(
                      (best, p) => (p.seconds > (best?.seconds ?? -1) ? p : best),
                      null as null | (typeof metrics.series)[number],
                    );
                    return peak && peak.seconds > 0
                      ? `Peak ${peak.date.format("MMM D")} · ${formatWatchTime(peak.seconds)} watched`
                      : "";
                  })()}
                </span>
              </div>
              <SecondsChart series={metrics.series} />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <VideoPanel id="panel-performance" title="Performance" action={header}>
      <div className="flex flex-col gap-[18px]">
        <div
          aria-busy={refreshing && status === "ready"}
          style={{
            opacity: refreshing && status === "ready" ? 0.55 : 1,
            transition: "opacity 150ms ease",
          }}
        >
          {body()}
        </div>
        {/* Below sm the numbers lead and the controls that scope them sit
            underneath. Outside `body()` so it holds in loading and error too —
            those are the two states a reader is most likely to answer by
            changing a filter, and the panel has never let a state switch them
            off. */}
        {!wide && controls}
      </div>
    </VideoPanel>
  );
};

export default PerformancePanel;
